import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { normalizeDropdown } from './dropdown';

export interface ApiEnvelope<T = any> {
  success: boolean;
  message: string;
  statusCode: number;
  data: T;
}

// ---------- models ----------
export interface Client {
  id: number;
  clientCode: string; // unique — backend 409 on dup
  clientName: string;
  companyName?: string | null;
  contactPerson?: string | null;
  email?: string | null;
  mobile?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  gstNo?: string | null;
  panNo?: string | null;
  logoUrl?: string | null;
  website?: string | null;
  remarks?: string | null;
  isActive: boolean;
  createdDate?: string | null;
  updatedDate?: string | null;
}
export type ClientDto = Partial<Client> & { clientCode: string; clientName: string };

export interface ClientBranch {
  id: number;
  clientId: number;
  branchCode: string;
  branchName: string;
  contactPerson?: string | null;
  email?: string | null;
  mobile?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  gstNo?: string | null;
  isDefault: boolean; // only one default per client
  isActive: boolean;
  clientName?: string | null; // joined (read-only)
}
export type ClientBranchDto = Partial<ClientBranch> & {
  clientId: number;
  branchCode: string;
  branchName: string;
};

export type ApiKeyEnvironment = 'Test' | 'Prod' | 'Live';
export interface ClientApiKey {
  id: number;
  clientId: number;
  // Branch binding. null = an ACCOUNT key (all branches, narrowable per request);
  // set = the key is LOCKED to that one branch.
  branchId?: number | null;
  branchCode?: string | null;
  branchName?: string | null; // joined (read-only)
  isDefaultBranch?: boolean;
  environment: ApiKeyEnvironment; // fixed at creation; baked into the key prefix
  apiKey?: string | null; // readable, env-prefixed e.g. "lapitch_live_…"
  apiSecret?: string | null; // returned ONLY once, right after Insert/Regenerate (stored hashed at rest)
  // PUBLIC embed key (e.g. "pk_live_…") for the drop-in enrollment widget — safe to expose in a website;
  // it can ONLY start an enrollment session. Origin-locked to embedAllowedOrigins (CSV; blank = any origin).
  publicEmbedKey?: string | null;
  embedAllowedOrigins?: string | null; // CSV of website origins the widget may run from
  allowedIPs?: string | null; // CSV
  rateLimitPerMinute?: number | null;
  expiryDate?: string | null; // yyyy-MM-dd
  isActive: boolean;
  createdDate?: string | null;
}
export type ClientApiKeyDto = Partial<ClientApiKey> & { clientId: number; environment: string };

/** A login for the CLIENT PORTAL (dbo.ClientPortal_User), managed here by an Arogya admin. */
export interface ClientPortalUser {
  id: number;
  branchId: number | null;
  roleId: number | null;
  username: string;
  fullName: string | null;
  email: string | null;
  mobile: string | null;
  isClientAdmin: boolean;
  isActive: boolean;
  mustChangePassword?: boolean;
  lastLoginAt?: string | null;
}

export interface ClientPaymentConfiguration {
  id: number;
  clientId: number;
  paymentIntegrationId: number; // dropdown: api/Dropdown/PaymentIntegrations
  paymentLinkExpiryMinutes?: number | null;
  currencyCode?: string | null; // e.g. INR
  allowPartialPayment: boolean;
  allowExpiredPayment: boolean;
  autoGeneratePaymentLink: boolean;
  autoGeneratePolicy: boolean;
  paymentDescription?: string | null;
  paymentSuccessUrl?: string | null;
  paymentFailureUrl?: string | null;
  isActive: boolean;
  paymentIntegrationName?: string | null; // joined (read-only)
}
export type ClientPaymentConfigurationDto = Partial<ClientPaymentConfiguration> & {
  clientId: number;
  paymentIntegrationId: number;
};

export interface ClientPremiumAccess {
  id: number;
  clientID: number;
  premiumID: number;
  isEnable: boolean;
  premiumCode?: string | null; // joined display
  coverageType?: string | null; // joined display
  createdDate?: string | null;
}
export interface ClientPremiumAssignRequest {
  clientID: number;
  premiumIds: number[];
  isEnable: boolean;
}
export interface ClientPremiumAccessDto {
  id?: number;
  clientID: number;
  premiumID: number;
  isEnable: boolean;
}

/** A client row with its message-channel preferences (ClientMessageType). */
export interface ClientMessageRow {
  clientId: number;
  clientCode?: string | null;
  clientName?: string | null;
  contactPerson?: string | null;
  email?: string | null;
  mobile?: string | null;
  messageTypeId?: number | null; // null => no row yet (treat as all-off)
  isWhatsApp: boolean;
  isSMS: boolean;
  isEmail: boolean;
  // Message-type toggles (which kinds of message to send). Default on.
  isPaymentReceipt: boolean;
  isCoi: boolean;
  isOtp: boolean;
  isPaymentLink: boolean;
  isPolicyIssued: boolean;
  isKyc: boolean;
}
export interface SaveClientMessageRequest {
  clientId: number;
  isWhatsApp: boolean;
  isSMS: boolean;
  isEmail: boolean;
  isPaymentReceipt: boolean;
  isCoi: boolean;
  isOtp: boolean;
  isPaymentLink: boolean;
  isPolicyIssued: boolean;
  isKyc: boolean;
}

/** Known result-callback events; free text is allowed for events that ship later. */
export type CallbackEvent = 'payment.success' | (string & {});

/** A client result-callback registration — the URL we call when eventName fires for the client. */
export interface ClientCallback {
  id: number;
  clientId: number;
  eventName: string; // e.g. payment.success
  callbackUrl: string;
  httpMethod?: string | null; // POST (default) | PUT | PATCH | GET
  authenticationType?: string | null; // informational label, e.g. Bearer / ApiKey
  headerKey?: string | null; // auth header name, e.g. Authorization
  headerValue?: string | null; // auth secret — mask in grids, reveal only in the edit dialog
  timeoutInSeconds: number; // 1..60
  retryCount: number; // 1..5
  isActive: boolean;
  isDeleted?: boolean;
  createdDate?: string | null;
  updatedDate?: string | null;
  clientName?: string | null; // joined (read-only)
  clientCode?: string | null; // joined (read-only)
}
export type ClientCallbackDto = Partial<ClientCallback> & {
  clientId: number;
  eventName: string;
  callbackUrl: string;
};

/** One delivery attempt of a callback (Client_CallbackLog row). */
export interface ClientCallbackLog {
  id: number;
  clientId: number;
  callbackId?: number | null;
  eventName?: string | null;
  referenceId?: number | null; // usually the memberId
  referenceType?: string | null; // e.g. "member"
  callbackUrl?: string | null;
  requestJson?: string | null; // the payload we sent
  responseJson?: string | null; // the client's response body (capped)
  httpStatusCode?: number | null; // null => transport error / never reached
  retryCount?: number | null; // attempts beyond the first
  isSuccess: boolean;
  createdDate?: string | null;
  clientName?: string | null;
  clientCode?: string | null;
}

export interface PagedResult<T> {
  page: number;
  pageSize: number;
  total: number;
  items: T[];
}

// ---------- defensive PascalCase->camelCase (copy the partner-premium-access.ts pattern) ----------
const pick = (x: any, ...keys: string[]) => {
  for (const k of keys) if (x?.[k] != null) return x[k];
  return null;
};
const asBool = (v: any) => v === true || v === 1 || v === '1' || v === 'true';

function mapEnvelope<T>(res: any, rowMapper?: (x: any) => T) {
  if (!rowMapper) return res;
  if (Array.isArray(res?.data)) return { ...res, data: res.data.map(rowMapper) };
  if (res?.data && typeof res.data === 'object') return { ...res, data: rowMapper(res.data) };
  return res;
}

function mapClient(x: any): Client {
  return {
    id: pick(x, 'Id', 'id'),
    clientCode: pick(x, 'ClientCode', 'clientCode'),
    clientName: pick(x, 'ClientName', 'clientName'),
    companyName: pick(x, 'CompanyName', 'companyName'),
    contactPerson: pick(x, 'ContactPerson', 'contactPerson'),
    email: pick(x, 'Email', 'email'),
    mobile: pick(x, 'Mobile', 'mobile'),
    address: pick(x, 'Address', 'address'),
    city: pick(x, 'City', 'city'),
    state: pick(x, 'State', 'state'),
    country: pick(x, 'Country', 'country'),
    pincode: pick(x, 'Pincode', 'pincode'),
    gstNo: pick(x, 'GstNo', 'gstNo'),
    panNo: pick(x, 'PanNo', 'panNo'),
    logoUrl: pick(x, 'LogoUrl', 'logoUrl'),
    website: pick(x, 'Website', 'website'),
    remarks: pick(x, 'Remarks', 'remarks'),
    isActive: asBool(pick(x, 'IsActive', 'isActive') ?? true),
    createdDate: pick(x, 'CreatedDate', 'createdDate'),
    updatedDate: pick(x, 'UpdatedDate', 'updatedDate'),
  };
}

function mapBranch(x: any): ClientBranch {
  return {
    id: pick(x, 'Id', 'id'),
    clientId: pick(x, 'ClientId', 'clientId', 'ClientID', 'clientID'),
    branchCode: pick(x, 'BranchCode', 'branchCode'),
    branchName: pick(x, 'BranchName', 'branchName'),
    contactPerson: pick(x, 'ContactPerson', 'contactPerson'),
    email: pick(x, 'Email', 'email'),
    mobile: pick(x, 'Mobile', 'mobile'),
    address: pick(x, 'Address', 'address'),
    city: pick(x, 'City', 'city'),
    state: pick(x, 'State', 'state'),
    country: pick(x, 'Country', 'country'),
    pincode: pick(x, 'Pincode', 'pincode'),
    gstNo: pick(x, 'GstNo', 'gstNo'),
    isDefault: asBool(pick(x, 'IsDefault', 'isDefault')),
    isActive: asBool(pick(x, 'IsActive', 'isActive') ?? true),
    clientName: pick(x, 'ClientName', 'clientName'),
  };
}

function mapApiKey(x: any): ClientApiKey {
  return {
    id: pick(x, 'Id', 'id'),
    clientId: pick(x, 'ClientId', 'clientId', 'ClientID', 'clientID'),
    branchId: pick(x, 'BranchId', 'branchId'),
    branchCode: pick(x, 'BranchCode', 'branchCode'),
    branchName: pick(x, 'BranchName', 'branchName'),
    isDefaultBranch: asBool(pick(x, 'IsDefaultBranch', 'isDefaultBranch') ?? false),
    environment: (pick(x, 'Environment', 'environment') ?? 'Test') as ApiKeyEnvironment,
    apiKey: pick(x, 'ApiKey', 'apiKey'),
    apiSecret: pick(x, 'ApiSecret', 'apiSecret'),
    publicEmbedKey: pick(x, 'PublicEmbedKey', 'publicEmbedKey'),
    embedAllowedOrigins: pick(x, 'EmbedAllowedOrigins', 'embedAllowedOrigins'),
    allowedIPs: pick(x, 'AllowedIPs', 'allowedIPs', 'AllowedIps', 'allowedIps'),
    rateLimitPerMinute: pick(x, 'RateLimitPerMinute', 'rateLimitPerMinute'),
    expiryDate: pick(x, 'ExpiryDate', 'expiryDate'),
    isActive: asBool(pick(x, 'IsActive', 'isActive') ?? true),
    createdDate: pick(x, 'CreatedDate', 'createdDate'),
  };
}

function mapPaymentConfig(x: any): ClientPaymentConfiguration {
  return {
    id: pick(x, 'Id', 'id'),
    clientId: pick(x, 'ClientId', 'clientId', 'ClientID', 'clientID'),
    paymentIntegrationId: pick(x, 'PaymentIntegrationId', 'paymentIntegrationId'),
    paymentIntegrationName: pick(x, 'PaymentIntegrationName', 'paymentIntegrationName'),
    paymentLinkExpiryMinutes: pick(x, 'PaymentLinkExpiryMinutes', 'paymentLinkExpiryMinutes'),
    currencyCode: pick(x, 'CurrencyCode', 'currencyCode'),
    allowPartialPayment: asBool(pick(x, 'AllowPartialPayment', 'allowPartialPayment')),
    allowExpiredPayment: asBool(pick(x, 'AllowExpiredPayment', 'allowExpiredPayment')),
    autoGeneratePaymentLink: asBool(pick(x, 'AutoGeneratePaymentLink', 'autoGeneratePaymentLink')),
    autoGeneratePolicy: asBool(pick(x, 'AutoGeneratePolicy', 'autoGeneratePolicy')),
    paymentDescription: pick(x, 'PaymentDescription', 'paymentDescription'),
    paymentSuccessUrl: pick(x, 'PaymentSuccessUrl', 'paymentSuccessUrl'),
    paymentFailureUrl: pick(x, 'PaymentFailureUrl', 'paymentFailureUrl'),
    isActive: asBool(pick(x, 'IsActive', 'isActive') ?? true),
  };
}

function mapPremiumAccess(x: any): ClientPremiumAccess {
  return {
    id: pick(x, 'Id', 'id'),
    clientID: pick(x, 'ClientID', 'clientID', 'ClientId', 'clientId'),
    premiumID: pick(x, 'PremiumID', 'premiumID', 'PremiumId', 'premiumId'),
    isEnable: asBool(pick(x, 'IsEnable', 'isEnable') ?? true),
    premiumCode: pick(x, 'PremiumCode', 'premiumCode'),
    coverageType: pick(x, 'CoverageType', 'coverageType'),
    createdDate: pick(x, 'CreatedDate', 'createdDate'),
  };
}

function mapMessageRow(x: any): ClientMessageRow {
  return {
    clientId: pick(x, 'ClientId', 'clientId', 'ClientID', 'clientID'),
    clientCode: pick(x, 'ClientCode', 'clientCode'),
    clientName: pick(x, 'ClientName', 'clientName'),
    contactPerson: pick(x, 'ContactPerson', 'contactPerson'),
    email: pick(x, 'Email', 'email'),
    mobile: pick(x, 'Mobile', 'mobile'),
    messageTypeId: pick(x, 'MessageTypeId', 'messageTypeId'),
    isWhatsApp: asBool(pick(x, 'IsWhatsApp', 'isWhatsApp')),
    isSMS: asBool(pick(x, 'IsSMS', 'isSMS')),
    isEmail: asBool(pick(x, 'IsEmail', 'isEmail')),
    isPaymentReceipt: asBool(pick(x, 'IsPaymentReceipt', 'isPaymentReceipt') ?? true),
    isCoi: asBool(pick(x, 'IsCoi', 'isCoi') ?? true),
    isOtp: asBool(pick(x, 'IsOtp', 'isOtp') ?? true),
    isPaymentLink: asBool(pick(x, 'IsPaymentLink', 'isPaymentLink') ?? true),
    isPolicyIssued: asBool(pick(x, 'IsPolicyIssued', 'isPolicyIssued') ?? true),
    isKyc: asBool(pick(x, 'IsKyc', 'isKyc') ?? true),
  };
}

function mapCallback(x: any): ClientCallback {
  return {
    id: pick(x, 'Id', 'id'),
    clientId: pick(x, 'ClientId', 'clientId', 'ClientID', 'clientID'),
    eventName: pick(x, 'EventName', 'eventName'),
    callbackUrl: pick(x, 'CallbackUrl', 'callbackUrl'),
    httpMethod: pick(x, 'HttpMethod', 'httpMethod'),
    authenticationType: pick(x, 'AuthenticationType', 'authenticationType'),
    headerKey: pick(x, 'HeaderKey', 'headerKey'),
    headerValue: pick(x, 'HeaderValue', 'headerValue'),
    timeoutInSeconds: pick(x, 'TimeoutInSeconds', 'timeoutInSeconds') ?? 15,
    retryCount: pick(x, 'RetryCount', 'retryCount') ?? 1,
    isActive: asBool(pick(x, 'IsActive', 'isActive') ?? true),
    isDeleted: asBool(pick(x, 'IsDeleted', 'isDeleted')),
    createdDate: pick(x, 'CreatedDate', 'createdDate'),
    updatedDate: pick(x, 'UpdatedDate', 'updatedDate'),
    clientName: pick(x, 'ClientName', 'clientName'),
    clientCode: pick(x, 'ClientCode', 'clientCode'),
  };
}

function mapCallbackLog(x: any): ClientCallbackLog {
  return {
    id: pick(x, 'Id', 'id'),
    clientId: pick(x, 'ClientId', 'clientId', 'ClientID', 'clientID'),
    callbackId: pick(x, 'CallbackId', 'callbackId'),
    eventName: pick(x, 'EventName', 'eventName'),
    referenceId: pick(x, 'ReferenceId', 'referenceId'),
    referenceType: pick(x, 'ReferenceType', 'referenceType'),
    callbackUrl: pick(x, 'CallbackUrl', 'callbackUrl'),
    requestJson: pick(x, 'RequestJson', 'requestJson'),
    responseJson: pick(x, 'ResponseJson', 'responseJson'),
    httpStatusCode: pick(x, 'HttpStatusCode', 'httpStatusCode'),
    retryCount: pick(x, 'RetryCount', 'retryCount') ?? 0,
    isSuccess: asBool(pick(x, 'IsSuccess', 'isSuccess')),
    createdDate: pick(x, 'CreatedDate', 'createdDate'),
    clientName: pick(x, 'ClientName', 'clientName'),
    clientCode: pick(x, 'ClientCode', 'clientCode'),
  };
}

@Injectable({ providedIn: 'root' })
export class ClientService {
  private http = inject(HttpClient);
  private readonly api = environment.apiUrl;

  // ---- Clients (api/Client) ----
  cltGetAll(search?: string) {
    return this.http
      .get<ApiEnvelope<Client[]>>(`${this.api}/Client/GetAll`, search ? { params: { search } } : {})
      .pipe(map((res) => mapEnvelope(res, mapClient)));
  }
  cltGetById(id: number) {
    return this.http
      .get<ApiEnvelope<Client>>(`${this.api}/Client/GetById`, { params: { id } })
      .pipe(map((res) => mapEnvelope(res, mapClient)));
  }
  cltSave(m: ClientDto) {
    const verb = (m as any).id ? 'Update' : 'Insert';
    return this.http.post<ApiEnvelope>(`${this.api}/Client/${verb}`, m);
  }
  cltSetActive(id: number, on: boolean) {
    return this.http.get<ApiEnvelope>(`${this.api}/Client/${on ? 'Enable' : 'Disable'}`, {
      params: { id },
    });
  }
  cltDelete(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/Client/Delete`, { params: { id } });
  }

  // ---- Branches (api/ClientBranch) ----
  brGetAll() {
    return this.http
      .get<ApiEnvelope<ClientBranch[]>>(`${this.api}/ClientBranch/GetAll`)
      .pipe(map((res) => mapEnvelope(res, mapBranch)));
  }
  brGetById(id: number) {
    return this.http
      .get<ApiEnvelope<ClientBranch>>(`${this.api}/ClientBranch/GetById`, { params: { id } })
      .pipe(map((res) => mapEnvelope(res, mapBranch)));
  }
  brGetByClient(clientId: number) {
    return this.http
      .get<ApiEnvelope<ClientBranch[]>>(`${this.api}/ClientBranch/GetByClient`, {
        params: { clientId },
      })
      .pipe(map((res) => mapEnvelope(res, mapBranch)));
  }
  brSave(m: ClientBranchDto) {
    const verb = (m as any).id ? 'Update' : 'Insert';
    return this.http.post<ApiEnvelope>(`${this.api}/ClientBranch/${verb}`, m);
  }
  brSetActive(id: number, on: boolean) {
    return this.http.get<ApiEnvelope>(`${this.api}/ClientBranch/${on ? 'Enable' : 'Disable'}`, {
      params: { id },
    });
  }
  brDelete(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ClientBranch/Delete`, { params: { id } });
  }

  // ---- Branch <-> RO mapping (api/ClientBranchRo) ----
  brRoGetByBranch(branchId: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ClientBranchRo/GetByBranch`, {
      params: { branchId },
    });
  }
  brRoSet(branchId: number, roIds: number[]) {
    return this.http.post<ApiEnvelope>(`${this.api}/ClientBranchRo/Set`, { branchId, roIds });
  }

  // ---- API keys (api/ClientApiKey) ----
  /** Env options for the key dropdown (Test / Prod / Live) — normalized to { value, label }. */
  keyEnvironments() {
    return this.http
      .get<ApiEnvelope<{ value: any; text: string }[]>>(`${this.api}/ClientApiKey/Environments`)
      .pipe(map(normalizeDropdown));
  }
  keyGetByClient(clientId: number) {
    return this.http
      .get<ApiEnvelope<ClientApiKey[]>>(`${this.api}/ClientApiKey/GetByClient`, {
        params: { clientId },
      })
      .pipe(map((res) => mapEnvelope(res, mapApiKey)));
  }
  keySave(m: ClientApiKeyDto) {
    const verb = (m as any).id ? 'Update' : 'Insert';
    return this.http.post<ApiEnvelope>(`${this.api}/ClientApiKey/${verb}`, m);
  }
  keyRegenerate(id: number) {
    return this.http.get<ApiEnvelope<{ apiKey: string; apiSecret: string }>>(
      `${this.api}/ClientApiKey/Regenerate`,
      { params: { id } },
    );
  }
  keySetActive(id: number, on: boolean) {
    return this.http.get<ApiEnvelope>(`${this.api}/ClientApiKey/${on ? 'Enable' : 'Disable'}`, {
      params: { id },
    });
  }
  keyDelete(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ClientApiKey/Delete`, { params: { id } });
  }

  // ---- Payment configuration (api/ClientPaymentConfiguration) — ONE per client ----
  pcGetByClient(clientId: number) {
    return this.http
      .get<ApiEnvelope<ClientPaymentConfiguration>>(
        `${this.api}/ClientPaymentConfiguration/GetByClient`,
        { params: { clientId } },
      )
      .pipe(map((res) => mapEnvelope(res, mapPaymentConfig)));
  }
  pcSave(m: ClientPaymentConfigurationDto) {
    const verb = (m as any).id ? 'Update' : 'Insert';
    return this.http.post<ApiEnvelope>(`${this.api}/ClientPaymentConfiguration/${verb}`, m);
  }
  pcSetActive(id: number, on: boolean) {
    return this.http.get<ApiEnvelope>(
      `${this.api}/ClientPaymentConfiguration/${on ? 'Enable' : 'Disable'}`,
      { params: { id } },
    );
  }
  pcDelete(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ClientPaymentConfiguration/Delete`, {
      params: { id },
    });
  }

  // ---- Premium access (api/ClientPremiumAccess) — mirrors PartnerPremiumAccess ----
  paGetByClient(clientId: number) {
    return this.http
      .get<ApiEnvelope<ClientPremiumAccess[]>>(`${this.api}/ClientPremiumAccess/GetByClient`, {
        params: { clientId },
      })
      .pipe(map((res) => mapEnvelope(res, mapPremiumAccess)));
  }
  paAssign(body: ClientPremiumAssignRequest) {
    return this.http.post<ApiEnvelope>(`${this.api}/ClientPremiumAccess/Assign`, body);
  }
  paInsert(dto: ClientPremiumAccessDto) {
    return this.http.post<ApiEnvelope>(`${this.api}/ClientPremiumAccess/Insert`, dto);
  }
  paUpdate(dto: ClientPremiumAccessDto) {
    return this.http.post<ApiEnvelope>(`${this.api}/ClientPremiumAccess/Update`, dto);
  }
  paSetEnable(id: number, on: boolean) {
    return this.http.get<ApiEnvelope>(
      `${this.api}/ClientPremiumAccess/${on ? 'Enable' : 'Disable'}`,
      { params: { id } },
    );
  }
  paDelete(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ClientPremiumAccess/Delete`, { params: { id } });
  }

  // ---- Premium rule -> branch -> RO mapping (api/ClientPremiumRuleRo) ----
  prRoGetByClient(clientId: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ClientPremiumRuleRo/GetByClient`, {
      params: { clientId },
    });
  }
  prRoAssign(body: { clientId: number; premiumRuleId: number; branchId: number; roIds: number[] }) {
    return this.http.post<ApiEnvelope>(`${this.api}/ClientPremiumRuleRo/Assign`, body);
  }
  prRoSetEnable(id: number, on: boolean) {
    return this.http.get<ApiEnvelope>(
      `${this.api}/ClientPremiumRuleRo/${on ? 'Enable' : 'Disable'}`,
      { params: { id } },
    );
  }
  prRoDelete(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ClientPremiumRuleRo/Delete`, { params: { id } });
  }

  // ---- Messaging / SMS type (api/ClientMessage) ----
  msgGetAll(search?: string) {
    return this.http
      .get<ApiEnvelope<ClientMessageRow[]>>(
        `${this.api}/ClientMessage/GetAll`,
        search ? { params: { search } } : {},
      )
      .pipe(map((res) => mapEnvelope(res, mapMessageRow)));
  }
  msgGetByClient(clientId: number) {
    return this.http
      .get<ApiEnvelope<ClientMessageRow>>(`${this.api}/ClientMessage/GetByClient`, {
        params: { clientId },
      })
      .pipe(map((res) => mapEnvelope(res, mapMessageRow)));
  }
  msgSave(body: SaveClientMessageRequest) {
    return this.http.post<ApiEnvelope>(`${this.api}/ClientMessage/Save`, body);
  }

  // ---- Result callbacks (api/ClientCallback) — Admin-only on the backend ----
  cbGetAll() {
    return this.http
      .get<ApiEnvelope<ClientCallback[]>>(`${this.api}/ClientCallback/GetAll`)
      .pipe(map((res) => mapEnvelope(res, mapCallback)));
  }
  cbGetById(id: number) {
    return this.http
      .get<ApiEnvelope<ClientCallback>>(`${this.api}/ClientCallback/GetById`, { params: { id } })
      .pipe(map((res) => mapEnvelope(res, mapCallback)));
  }
  cbGetByClient(clientId: number) {
    return this.http
      .get<ApiEnvelope<ClientCallback[]>>(`${this.api}/ClientCallback/GetByClient`, {
        params: { clientId },
      })
      .pipe(map((res) => mapEnvelope(res, mapCallback)));
  }
  cbSave(m: ClientCallbackDto) {
    const verb = (m as any).id ? 'Update' : 'Insert';
    return this.http.post<ApiEnvelope>(`${this.api}/ClientCallback/${verb}`, m);
  }
  cbSetActive(id: number, on: boolean) {
    return this.http.get<ApiEnvelope>(`${this.api}/ClientCallback/${on ? 'Enable' : 'Disable'}`, {
      params: { id },
    });
  }
  cbDelete(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ClientCallback/Delete`, { params: { id } });
  }
  /** Delivery log, newest first, paged server-side. Both filters are optional. */
  cbGetLogs(clientId?: number, callbackId?: number, page = 1, pageSize = 50) {
    const params: any = { page, pageSize };
    if (clientId != null) params.clientId = clientId;
    if (callbackId != null) params.callbackId = callbackId;
    return this.http
      .get<ApiEnvelope<PagedResult<ClientCallbackLog>>>(`${this.api}/ClientCallback/GetLogs`, {
        params,
      })
      .pipe(
        map((res: any) => {
          const d = res?.data ?? {};
          const items = pick(d, 'Items', 'items') ?? [];
          const paged: PagedResult<ClientCallbackLog> = {
            page: pick(d, 'Page', 'page') ?? page,
            pageSize: pick(d, 'PageSize', 'pageSize') ?? pageSize,
            total: pick(d, 'Total', 'total') ?? 0,
            items: (Array.isArray(items) ? items : []).map(mapCallbackLog),
          };
          return { ...res, data: paged } as ApiEnvelope<PagedResult<ClientCallbackLog>>;
        }),
      );
  }

  // ---- Dropdowns (api/Dropdown) — returns { value, text }; normalized to { value, label } ----
  ddClients() {
    return this.http
      .get<ApiEnvelope<{ value: number; text: string }[]>>(`${this.api}/Dropdown/Clients`)
      .pipe(map(normalizeDropdown));
  }
  ddPaymentIntegrations() {
    return this.http
      .get<ApiEnvelope<{ value: number; text: string }[]>>(
        `${this.api}/Dropdown/PaymentIntegrations`,
      )
      .pipe(map(normalizeDropdown));
  }

  // ---- Client portal logins (api/ClientPortalManage/Admin/*) ----
  // Admin-only bootstrap: list a client's portal users, create the first client-admin
  // login, and reset passwords. The password is sent in plain text over TLS and hashed
  // server-side (AuthHelper.HashPassword) — it is never stored or echoed back.
  portalUsers(clientId: number) {
    return this.http.get<ApiEnvelope<ClientPortalUser[]>>(
      `${this.api}/ClientPortalManage/Admin/Users`,
      { params: { clientId } },
    );
  }

  portalUserCreate(clientId: number, model: Partial<ClientPortalUser> & { password: string }) {
    return this.http.post<ApiEnvelope>(
      `${this.api}/ClientPortalManage/Admin/Users/Insert`,
      model,
      { params: { clientId } },
    );
  }

  portalUserResetPassword(clientId: number, id: number, password: string) {
    return this.http.post<ApiEnvelope>(
      `${this.api}/ClientPortalManage/Admin/Users/ResetPassword`,
      { id, password },
      { params: { clientId } },
    );
  }

  portalLoginLogs(clientId: number, page = 1, pageSize = 10) {
    return this.http.get<ApiEnvelope<any>>(
      `${this.api}/ClientPortalManage/Admin/LoginLogs`,
      { params: { clientId, page, pageSize } },
    );
  }
}
